<template>
  <FhForm
    ref="refWaiverForm"
    v-model="waiverForm"
    name="waiverForm"
    data-test-id="fh-waiver-form"
    class="waiver-form"
    @submit:error="onErrorForm"
    @submit="submitForm"
  >
    <ContentPanel>
      <ContentWrapper centered-content is-title>
        <FhText tag="h1" variant="display-xl" data-test-id="waiver-title">
          {{ props.waiver.title }}
        </FhText>
      </ContentWrapper>
    </ContentPanel>

    <EventInfoBlock :booking="props.booking" :company="props.company" />

    <ContentPanel>
      <ContentWrapper
        v-if="props.waiver.instructions"
        border-bottom
        class="content-block additional-info-block"
      >
        <FhTitle tag="h2" variant="display-md">
          {{ T('Instructions') }}
        </FhTitle>

        <FhRichText
          :markdown="props.waiver.instructions"
          data-test-id="waiver-instruction-text"
        />
      </ContentWrapper>

      <ContentWrapper v-if="!props.isDockDevice" class="content-block">
        <FhText variant="body-md">
          {{ T('Share with other participants') }}
        </FhText>

        <CopyButton :preview-mode="props.previewMode" />
      </ContentWrapper>
    </ContentPanel>

    <ContentPanel>
      <ContentWrapper>
        <FhRichText
          :markdown="props.waiver.body"
          data-test-id="waiver-body-text"
        />
      </ContentWrapper>

      <!-- Body acknowledgement (when sections exist, body is Section 1) -->
      <ContentWrapper
        v-if="
          props.waiver.sections?.length &&
          props.waiver.features?.sectionAcknowledgementType
        "
        class="content-block ack-block"
      >
        <FhCheckbox
          v-if="props.waiver.features.sectionAcknowledgementType === 'checkbox'"
          v-model="waiverForm.bodyAck"
          name="bodyAck"
          data-test-id="body-ack-checkbox"
          :validators="[Required(T('You must acknowledge this section'))]"
          @input="broadcastUserActivity"
          @change="broadcastUserActivity"
        >
          <FhLabel>
            {{ props.waiver.features.sectionAcknowledgementText }}
          </FhLabel>
        </FhCheckbox>

        <FhTextInput
          v-else-if="
            props.waiver.features.sectionAcknowledgementType === 'initials'
          "
          v-model="waiverForm.bodyInitials"
          name="bodyInitials"
          data-test-id="body-ack-initials"
          max-width="200px"
          :validators="[
            Required(T('Enter your initials')),
            createMaxLengthValidator(100),
          ]"
          @input="broadcastUserActivity"
          @change="broadcastUserActivity"
        >
          <FhLabel>{{ T('Initials') }}</FhLabel>
        </FhTextInput>
      </ContentWrapper>
    </ContentPanel>

    <!-- Waiver Sections -->
    <template v-if="props.waiver.sections?.length">
      <ContentPanel
        v-for="(section, sectionIndex) in props.waiver.sections"
        :key="`section-${sectionIndex}`"
      >
        <ContentWrapper class="content-block">
          <FhRichText
            :markdown="section.body"
            :data-test-id="`waiver-section-body-${sectionIndex}`"
          />
        </ContentWrapper>

        <ContentWrapper
          v-if="props.waiver.features?.sectionAcknowledgementType"
          class="content-block ack-block"
        >
          <FhCheckbox
            v-if="
              props.waiver.features.sectionAcknowledgementType === 'checkbox'
            "
            v-model="
              waiverForm.sectionAcknowledgements[sectionIndex].acknowledged
            "
            :name="`sectionAcknowledgements[${sectionIndex}].acknowledged`"
            :data-test-id="`section-ack-checkbox-${sectionIndex}`"
            :validators="[Required(T('You must acknowledge this section'))]"
            @input="broadcastUserActivity"
            @change="broadcastUserActivity"
          >
            <FhLabel>
              {{ props.waiver.features.sectionAcknowledgementText }}
            </FhLabel>
          </FhCheckbox>

          <FhTextInput
            v-else-if="
              props.waiver.features.sectionAcknowledgementType === 'initials'
            "
            v-model="waiverForm.sectionAcknowledgements[sectionIndex].initials"
            :name="`sectionAcknowledgements[${sectionIndex}].initials`"
            :data-test-id="`section-ack-initials-${sectionIndex}`"
            max-width="200px"
            :validators="[
              Required(T('Enter your initials')),
              createMaxLengthValidator(100),
            ]"
            @input="broadcastUserActivity"
            @change="broadcastUserActivity"
          >
            <FhLabel>{{ T('Initials') }}</FhLabel>
          </FhTextInput>
        </ContentWrapper>
      </ContentPanel>
    </template>

    <ContentPanel v-if="props.waiver.features?.isMinorsEnabled">
      <ContentWrapper class="content-block">
        <FhCheckbox
          name="isMinorChecked"
          data-test-id="fh-input-minor-checkbox"
          @input="broadcastUserActivity"
          @change="broadcastUserActivity"
        >
          <FhLabel data-test-id="fh-waiver-minor-text">
            {{ computedMinorText }}
          </FhLabel>
        </FhCheckbox>

        <FhAlert variant="information">
          <FhText data-test-id="fh-waiver-minor-info-text">
            {{ T('We always require an adult to sign on behalf of a minor') }}
          </FhText>
        </FhAlert>
      </ContentWrapper>
    </ContentPanel>

    <ContentPanel v-if="waiverForm.minors?.length">
      <ContentWrapper
        v-for="(minor, index) in waiverForm.minors"
        :key="`${minor.key}-${index}`"
        class="minor-content-container"
      >
        <div class="content-block">
          <div class="minor-block-item-title">
            <FhTitle tag="h2" variant="display-md">
              {{ getMinorItemTitleName(index) }}
            </FhTitle>

            <FhIconButton
              v-if="waiverForm.minors.length > 1"
              variant="plain"
              size="sm"
              :icon="close"
              @click="onClickDeleteMinor(index)"
            >
              {{ T('Delete') }}
            </FhIconButton>
          </div>

          <div class="names-block">
            <div>
              <FhTextInput
                v-model="minor.firstName"
                :name="`minors[${index}].firstName`"
                :data-test-id="`fh-input-minor-first-name-${index}`"
                :validators="[
                  Required(T('Enter first name')),
                  createMaxLengthValidator(64),
                ]"
                @input="broadcastUserActivity"
                @change="broadcastUserActivity"
              >
                <FhLabel>
                  {{ T('First name') }}
                </FhLabel>
              </FhTextInput>
            </div>

            <div>
              <FhTextInput
                v-model="minor.lastName"
                :name="`minors[${index}].lastName`"
                :data-test-id="`fh-input-minor-last-name-${index}`"
                :validators="[
                  Required(T('Enter last name')),
                  createMaxLengthValidator(64),
                ]"
                @input="broadcastUserActivity"
                @change="broadcastUserActivity"
              >
                <FhLabel>
                  {{ T('Last name') }}
                </FhLabel>
              </FhTextInput>
            </div>
          </div>

          <div class="flex-container">
            <div class="half-width-container">
              <FhDatepicker
                v-model="minor.dob"
                :name="`minors[${index}].dob`"
                :data-test-id="`fh-input-minor-dob-${index}`"
                :locale="locale"
                :max="maxDate"
                :validators="[Required(T('Enter valid date of birth'))]"
                @input="broadcastUserActivity"
                @change="broadcastUserActivity"
              >
                <FhLabel>
                  {{ T('Date of birth') }}
                </FhLabel>
              </FhDatepicker>
            </div>
          </div>
        </div>

        <div>
          <FhButton
            v-if="index + 1 === waiverForm.minors.length"
            data-test-id="fh-button-add-another-minor"
            variant="tertiary"
            :icon="plus"
            @click="onClickAddMinor"
          >
            {{ T('Add another minor') }}
          </FhButton>
        </div>
      </ContentWrapper>
    </ContentPanel>

    <ContentPanel>
      <ContentWrapper class="content-block">
        <FhTitle tag="h2" variant="display-md">
          {{ getContactDetailsTitleName }}
        </FhTitle>

        <div class="names-block">
          <div>
            <FhTextInput
              name="firstName"
              data-test-id="fh-input-first-name"
              :validators="[
                Required(T('Enter first name')),
                createMaxLengthValidator(64),
              ]"
              @input="broadcastUserActivity"
              @change="broadcastUserActivity"
            >
              <FhLabel>
                {{ T('First name') }}
              </FhLabel>
            </FhTextInput>
          </div>

          <div>
            <FhTextInput
              name="lastName"
              data-test-id="fh-input-last-name"
              :validators="[
                Required(T('Enter last name')),
                createMaxLengthValidator(64),
              ]"
              @input="broadcastUserActivity"
              @change="broadcastUserActivity"
            >
              <FhLabel>
                {{ T('Last name') }}
              </FhLabel>
            </FhTextInput>
          </div>
        </div>

        <div v-if="props.waiver.features?.isDobEnabled" class="flex-container">
          <div class="half-width-container">
            <FhDatepicker
              name="dob"
              data-test-id="fh-input-dob"
              :locale="locale"
              :max="maxDate"
              :validators="[
                Required(T('Enter valid date of birth')),
                ValidateLegalDobRule(
                  computedDobValidationMessage,
                  props.waiver.features?.requiredLegalAge ||
                    defaultRequiredLegalAge,
                ),
              ]"
              @input="broadcastUserActivity"
              @change="broadcastUserActivity"
              @update:model-value="broadcastUserActivity"
            >
              <FhLabel>
                {{ T('Date of birth') }}
              </FhLabel>
            </FhDatepicker>
          </div>
        </div>

        <FhTextInput
          name="email"
          data-test-id="fh-input-email-address"
          :validators="[
            Required(T('Enter email address')),
            Email(T('Enter valid email address')),
            createMaxLengthValidator(254),
          ]"
          @input="broadcastUserActivity"
          @change="broadcastUserActivity"
        >
          <FhLabel>
            {{ T('Email address') }}
          </FhLabel>
        </FhTextInput>

        <div
          v-if="props.waiver.features?.isPhoneNumberEnabled"
          class="flex-container"
        >
          <div class="half-width-container">
            <FhPhoneInput
              name="phoneNumber"
              data-test-id="fh-input-phone-number"
              :validators="[
                Required(T('Enter valid phone number')),
                createMaxLengthValidator(32),
              ]"
              :initial-country="props.company.processorCountry"
              @input="broadcastUserActivity"
              @change="broadcastUserActivity"
            >
              <FhLabel>
                {{ T('Phone number') }}
              </FhLabel>
            </FhPhoneInput>
          </div>
        </div>

        <FhCheckbox
          name="isMarketingChecked"
          data-test-id="fh-waiver-marketing-checkbox"
          @input="broadcastUserActivity"
          @change="broadcastUserActivity"
        >
          <FhLabel data-test-id="fh-waiver-marketing-text">
            {{ props.waiver.marketingText }}

            <template v-if="props.company.policies" #helpText>
              <span>
                {{ T('By signing this waiver you agree to our policy.') }}
                <span class="text-link" @click.prevent="openPolicies">
                  {{ T('View policy') }}
                </span>
              </span>
            </template>
          </FhLabel>
        </FhCheckbox>

        <div
          v-if="props.waiver.features?.isAddressEnabled"
          class="address-block-container"
        >
          <FhTextInput
            name="addressLine"
            data-test-id="fh-input-street"
            :placeholder="T('Street')"
            :validators="[
              Required(T('Enter address')),
              createMaxLengthValidator(255),
            ]"
            @input="broadcastUserActivity"
            @change="broadcastUserActivity"
          >
            <FhLabel>
              {{ T('Home address') }}
            </FhLabel>
          </FhTextInput>

          <div class="names-block">
            <div>
              <FhTextInput
                name="city"
                data-test-id="fh-input-city"
                :placeholder="T('City')"
                :validators="[
                  Required(T('Enter city')),
                  createMaxLengthValidator(100),
                ]"
                @input="broadcastUserActivity"
                @change="broadcastUserActivity"
              />
            </div>

            <div>
              <FhTextInput
                name="stateProvince"
                data-test-id="fh-input-state"
                :placeholder="T('State or province')"
                :validators="[
                  Required(T('Enter state or province')),
                  createMaxLengthValidator(100),
                ]"
                @input="broadcastUserActivity"
                @change="broadcastUserActivity"
              />
            </div>

            <div>
              <FhTextInput
                name="postcode"
                data-test-id="fh-input-postal-code"
                hide-label
                :placeholder="T('Postal code')"
                :validators="[
                  Required(T('Enter postal code')),
                  createMaxLengthValidator(20),
                ]"
                @input="broadcastUserActivity"
                @change="broadcastUserActivity"
              />
            </div>
          </div>

          <FhSelect
            name="country"
            data-test-id="fh-select-country"
            :placeholder="T('Select country')"
            :validators="[Required(T('Select country'))]"
            :options="countries"
            @input="broadcastUserActivity"
            @change="broadcastUserActivity"
          />
        </div>

        <template v-if="props.waiver.features?.isDriversLicenseEnabled">
          <div class="names-block">
            <div>
              <FhTextInput
                name="driversLicenseNumber"
                data-test-id="fh-input-drivers-license-number"
                :validators="
                  props.waiver.features?.isDriversLicenseOptional
                    ? [createMaxLengthValidator(50)]
                    : [
                        Required(T('Enter driver’s license number')),
                        createMaxLengthValidator(50),
                      ]
                "
                @input="broadcastUserActivity"
                @change="broadcastUserActivity"
              >
                <FhLabel>
                  {{ T('Driver’s license number') }}
                </FhLabel>
              </FhTextInput>
            </div>

            <div>
              <FhTextInput
                name="driversLicenseIssuer"
                data-test-id="fh-input-drivers-license-issuer"
                :validators="
                  props.waiver.features?.isDriversLicenseOptional
                    ? [createMaxLengthValidator(100)]
                    : [
                        Required(
                          T('Enter driver’s license issue state or province'),
                        ),
                        createMaxLengthValidator(100),
                      ]
                "
                @input="broadcastUserActivity"
                @change="broadcastUserActivity"
              >
                <FhLabel>
                  {{ T('Driver’s license issue state or province') }}
                </FhLabel>
              </FhTextInput>
            </div>
          </div>

          <div v-if="isDriversLicenseRequired">
            <FhCheckbox
              name="isDriversLicenseChecked"
              data-test-id="fh-waiver-drivers-license-acceptance-checkbox"
              :validators="[Required(T('Checkbox is required'))]"
              @input="broadcastUserActivity"
              @change="broadcastUserActivity"
            >
              <FhLabel>
                <span v-if="props.company.policies">
                  {{
                    T(
                      "I consent to storage and use of my driver's license number in accordance with the policy.",
                    )
                  }}

                  <span class="text-link" @click.prevent="openPolicies">
                    {{ T('View policy') }}
                  </span>
                </span>

                <template v-else>
                  {{
                    T(
                      "I consent to storage and use of my driver's license number.",
                    )
                  }}
                </template>
              </FhLabel>
            </FhCheckbox>
          </div>
        </template>

        <div
          v-if="props.waiver.features?.heightData?.enabled"
          class="flex-container"
        >
          <div class="half-width-container">
            <div v-if="isHeightInUSAFormat" class="two-inputs-height-block">
              <div class="height-feet-input-wrapper">
                <FhTextInput
                  :aria-label="T('Height in feet')"
                  name="heightFeet"
                  data-test-id="fh-input-height-feet"
                  :validators="[
                    Required(T('Enter height in feet')),
                    createMinValueValidator(1),
                  ]"
                  type="number"
                  @input="broadcastUserActivity"
                  @change="broadcastUserActivity"
                >
                  <FhLabel>
                    {{ T('Height') }}
                  </FhLabel>

                  <template #suffix>
                    {{ HEIGHT_UNIT_LABEL.FT }}
                  </template>
                </FhTextInput>
              </div>

              <div class="height-inches-input-wrapper">
                <FhTextInput
                  :aria-label="T('Height in inches')"
                  name="heightInches"
                  data-test-id="fh-input-height-inches"
                  :validators="[
                    Required(T('Enter height in inches')),
                    createMinValueValidator(0),
                  ]"
                  type="number"
                  @input="broadcastUserActivity"
                  @change="broadcastUserActivity"
                >
                  <template #suffix>
                    {{ HEIGHT_UNIT_LABEL.IN }}
                  </template>
                </FhTextInput>
              </div>
            </div>

            <FhTextInput
              v-else
              :aria-label="
                interpolate(T('Height in %(unit)s'), {
                  unit: HEIGHT_UNIT_LABEL[
                    props.waiver.features.heightData.unit
                  ],
                })
              "
              name="height"
              data-test-id="fh-input-height"
              :validators="[
                Required(T('Enter Height')),
                createMinValueValidator(1),
              ]"
              type="number"
              @input="broadcastUserActivity"
              @change="broadcastUserActivity"
            >
              <FhLabel>
                {{ T('Height') }}
              </FhLabel>

              <template #suffix>
                {{ HEIGHT_UNIT_LABEL[props.waiver.features.heightData.unit] }}
              </template>
            </FhTextInput>
          </div>
        </div>

        <div
          v-if="props.waiver.features?.weightData?.enabled"
          class="flex-container"
        >
          <div class="half-width-container">
            <FhTextInput
              :aria-label="
                interpolate(T('Weight in %(unit)s'), {
                  unit: WEIGHT_UNIT_LABEL[
                    props.waiver.features.weightData.unit
                  ],
                })
              "
              name="weight"
              data-test-id="fh-input-weight"
              :validators="[
                Required(T('Enter weight')),
                createMinValueValidator(1),
              ]"
              type="number"
              @input="broadcastUserActivity"
              @change="broadcastUserActivity"
            >
              <FhLabel>
                {{ T('Weight') }}
              </FhLabel>

              <template #suffix>
                {{ WEIGHT_UNIT_LABEL[props.waiver.features.weightData.unit] }}
              </template>
            </FhTextInput>
          </div>
        </div>

        <div
          v-if="props.waiver.features?.shoeSizeData?.enabled"
          class="flex-container"
        >
          <div class="half-width-container">
            <FhTextInput
              :aria-label="
                interpolate(T('Shoe size in %(unit)s'), {
                  unit: SHOE_SIZE_UNIT_LABEL[
                    props.waiver.features.shoeSizeData.unit
                  ],
                })
              "
              name="shoeSize"
              data-test-id="fh-input-shoe-size"
              :validators="[
                Required(T('Enter shoe size')),
                createMinValueValidator(1),
              ]"
              type="number"
              @input="broadcastUserActivity"
              @change="broadcastUserActivity"
            >
              <FhLabel>
                {{ T('Shoe size') }}
              </FhLabel>

              <template #suffix>
                {{
                  SHOE_SIZE_UNIT_LABEL[props.waiver.features.shoeSizeData.unit]
                }}
              </template>
            </FhTextInput>
          </div>
        </div>
      </ContentWrapper>
    </ContentPanel>

    <ContentPanel
      v-if="
        props.waiver.features?.isEmergencyContactEnabled &&
        !waiverForm.minors?.length
      "
    >
      <ContentWrapper class="content-block">
        <FhTitle tag="h2" variant="display-md">
          {{ T('Emergency contact details') }}
        </FhTitle>

        <div class="names-block">
          <div>
            <FhTextInput
              name="emergencyContact.firstName"
              data-test-id="fh-input-emergency-contact-first-name"
              :validators="[
                Required(T('Enter first name')),
                createMaxLengthValidator(64),
              ]"
              @input="broadcastUserActivity"
              @change="broadcastUserActivity"
            >
              <FhLabel>
                {{ T('First name') }}
              </FhLabel>
            </FhTextInput>
          </div>

          <div>
            <FhTextInput
              name="emergencyContact.lastName"
              data-test-id="fh-input-emergency-contact-last-name"
              :validators="[
                Required(T('Enter last name')),
                createMaxLengthValidator(64),
              ]"
              @input="broadcastUserActivity"
              @change="broadcastUserActivity"
            >
              <FhLabel>
                {{ T('Last name') }}
              </FhLabel>
            </FhTextInput>
          </div>
        </div>

        <div class="flex-container">
          <div class="half-width-container">
            <FhPhoneInput
              name="emergencyContact.phoneNumber"
              data-test-id="fh-input-emergency-contact-phone-number"
              :validators="[
                Required(T('Enter valid phone number')),
                createMaxLengthValidator(32),
              ]"
              :initial-country="props.company.processorCountry"
              @input="broadcastUserActivity"
              @change="broadcastUserActivity"
            >
              <FhLabel>
                {{ T('Phone number') }}
              </FhLabel>
            </FhPhoneInput>
          </div>
        </div>

        <div
          v-if="props.waiver.features?.isEmergencyContactHomeAddressEnabled"
          class="address-block-container"
        >
          <FhTextInput
            name="emergencyContact.addressLine"
            data-test-id="fh-input-ec-street"
            :placeholder="T('Street')"
            :validators="[
              Required(T('Enter address')),
              createMaxLengthValidator(255),
            ]"
            @input="broadcastUserActivity"
            @change="broadcastUserActivity"
          >
            <FhLabel>
              {{ T('Home address') }}
            </FhLabel>
          </FhTextInput>

          <div class="names-block">
            <div>
              <FhTextInput
                name="emergencyContact.city"
                data-test-id="fh-input-ec-city"
                :placeholder="T('City')"
                :validators="[
                  Required(T('Enter city')),
                  createMaxLengthValidator(100),
                ]"
                @input="broadcastUserActivity"
                @change="broadcastUserActivity"
              />
            </div>

            <div>
              <FhTextInput
                name="emergencyContact.stateProvince"
                data-test-id="fh-input-ec-state"
                :placeholder="T('State or province')"
                :validators="[
                  Required(T('Enter state or province')),
                  createMaxLengthValidator(100),
                ]"
                @input="broadcastUserActivity"
                @change="broadcastUserActivity"
              />
            </div>

            <div>
              <FhTextInput
                name="emergencyContact.postcode"
                data-test-id="fh-input-postal-ec-code"
                hide-label
                :placeholder="T('Postal code')"
                :validators="[
                  Required(T('Enter postal code')),
                  createMaxLengthValidator(32),
                ]"
                @input="broadcastUserActivity"
                @change="broadcastUserActivity"
              />
            </div>
          </div>

          <FhSelect
            name="emergencyContact.country"
            data-test-id="fh-select-ec-country"
            :placeholder="T('Select country')"
            :validators="[Required(T('Select country'))]"
            :options="countries"
            @input="broadcastUserActivity"
            @change="broadcastUserActivity"
          />
        </div>
      </ContentWrapper>
    </ContentPanel>

    <ContentPanel v-if="props.waiver.features?.isAdditionalParticipantsEnabled">
      <ContentWrapper class="content-block">
        <FhCheckbox
          name="isAdditionalParticipantChecked"
          data-test-id="fh-input-additional-participant-checkbox"
          @input="broadcastUserActivity"
          @change="broadcastUserActivity"
        >
          <FhLabel data-test-id="fh-waiver-additional-participant-text">
            {{
              T(
                'This waiver contains additional participants. I confirm that I have obtained consent from each listed Participant to sign this waiver on their behalf.',
              )
            }}
          </FhLabel>
        </FhCheckbox>
      </ContentWrapper>
    </ContentPanel>

    <ContentPanel v-if="waiverForm.additionalParticipants?.length">
      <ContentWrapper
        v-for="(
          additionalParticipant, index
        ) in waiverForm.additionalParticipants"
        :key="`${additionalParticipant.key}-${index}`"
        class="additional-participant-content-container"
      >
        <div class="content-block">
          <div class="additional-participant-block-item-title">
            <FhTitle tag="h2" variant="display-md">
              {{ getAdditionalParticipantItemTitleName(index) }}
            </FhTitle>

            <FhIconButton
              v-if="waiverForm.additionalParticipants.length > 1"
              variant="plain"
              size="sm"
              :icon="close"
              @click="onClickDeleteAdditionalParticipant(index)"
            >
              {{ T('Delete') }}
            </FhIconButton>
          </div>

          <div class="names-block">
            <div>
              <FhTextInput
                v-model="additionalParticipant.firstName"
                :name="`additionalParticipants[${index}].firstName`"
                :data-test-id="`fh-input-additional-participant-first-name-${index}`"
                :validators="[
                  Required(T('Enter first name')),
                  createMaxLengthValidator(64),
                ]"
                @input="broadcastUserActivity"
                @change="broadcastUserActivity"
              >
                <FhLabel>
                  {{ T('First name') }}
                </FhLabel>
              </FhTextInput>
            </div>

            <div>
              <FhTextInput
                v-model="additionalParticipant.lastName"
                :name="`additionalParticipants[${index}].lastName`"
                :data-test-id="`fh-input-additional-participant-last-name-${index}`"
                :validators="[
                  Required(T('Enter last name')),
                  createMaxLengthValidator(64),
                ]"
                @input="broadcastUserActivity"
                @change="broadcastUserActivity"
              >
                <FhLabel>
                  {{ T('Last name') }}
                </FhLabel>
              </FhTextInput>
            </div>
          </div>
        </div>

        <div>
          <FhButton
            v-if="index + 1 === waiverForm.additionalParticipants.length"
            data-test-id="fh-button-add-another-additional-participant"
            variant="tertiary"
            :icon="plus"
            @click="onClickAddAdditionalParticipant"
          >
            {{ T('Add another participant') }}
          </FhButton>
        </div>
      </ContentWrapper>
    </ContentPanel>

    <ContentPanel>
      <ContentWrapper class="content-block">
        <FhTitle tag="h2" variant="display-md">
          {{ signatureBlockText }}
        </FhTitle>

        <template v-if="props.waiver.signatureType === 'acceptance_checkbox'">
          <FhCheckbox
            name="isAcceptanceChecked"
            data-test-id="fh-input-acceptance-checkbox"
            :validators="[Required(T('Signature is required'))]"
            @input="broadcastUserActivity"
            @change="broadcastUserActivity"
          >
            <FhLabel data-test-id="fh-waiver-acceptance-text">
              {{ props.waiver.acceptanceText }}
            </FhLabel>
          </FhCheckbox>
        </template>

        <template v-else>
          <div class="signature-subtitle">
            <FhText variant="body-md">
              {{ T('Use the box below to sign the waiver') }}
            </FhText>

            <FhButton variant="secondary" @click="onClickClearCanvasButton">
              {{ T('Clear signature') }}
            </FhButton>
          </div>

          <div class="signature-canvas-container">
            <canvas
              ref="signatureCanvas"
              class="signature-canvas"
              :class="{
                'signature-canvas-invalid': !!clientErrors.signatureCanvas,
              }"
            ></canvas>

            <FhText
              v-if="clientErrors.signatureCanvas"
              variant="body-sm"
              color="error-default"
              weight="medium"
              class="signature-canvas-validation-message"
            >
              <FhIcon size="xs" color="error-default" :icon="alertFilled" />

              {{ clientErrors.signatureCanvas }}
            </FhText>
          </div>
        </template>

        <div class="submit-button-container">
          <FhButton
            class="submit-button"
            data-test-id="fh-button-submit-waiver"
            size="lg"
            :disabled="props.previewMode"
            type="submit"
          >
            {{ T('Submit waiver') }}
          </FhButton>
        </div>
      </ContentWrapper>
    </ContentPanel>

    <TermsModal
      v-if="props.company.policies"
      ref="termsRef"
      :is-open="isShowTermsModal"
      :company-name="props.company.name"
      :company-terms="props.company.policies"
      @close="onCloseTermsModal"
    />
  </FhForm>
</template>

<script setup>
import { onMounted, onUnmounted, ref, computed, watch } from 'vue';
import moment from 'moment';
import SignaturePad from 'signature_pad';
import {
  FhButton,
  FhAlert,
  FhTitle,
  FhIconButton,
  FhText,
  FhForm,
  FhTextInput,
  FhSelect,
  FhPhoneInput,
  FhDatepicker,
  FhCheckbox,
  FhLabel,
  FhIcon,
  FhRichText,
} from '@fareharbor-com/beacon-vue';
import { plus, close, alertFilled } from '@fareharbor-com/beacon-vue/icons';
import { Required, Email } from '@fareharbor-com/beacon-vue/validators';
import ContentPanel from './shared/ContentPanel.vue';
import ContentWrapper from './shared/ContentWrapper.vue';
import EventInfoBlock from './shared/EventInfoBlock.vue';
import TermsModal from './shared/TermsModal.vue';
import CopyButton from './shared/copy-button/CopyButton.vue';
import {
  EMPTY_ERRORS_FOR_FIELDS,
  createMaxLengthValidator,
  createMinValueValidator,
} from './utils';
import { separateFormErrors } from '../utils/separateFormErrors';
import { ValidateLegalDobRule } from '../utils/form-validation-rules/validateLegalDobRule';
import { formatCountriesToSelectOptions } from '../constants/countries';
import { track } from '../scripts/mixpanel';
import { generateUuid } from '../../../website/static/js/services/utils';
import { nativeBroadcast } from '../utils/nativeBroadcast';
import {
  WEIGHT_UNIT_LABEL,
  SHOE_SIZE_UNIT_LABEL,
  HEIGHT_UNIT_LABEL,
  US_HEIGHT_UNIT,
} from '../../../website/static/js/components/fh-waivers/shared/constants';

const defaultRequiredLegalAge = 18;
const emit = defineEmits(['submit']);

// Debounce timer for user activity broadcasting
let activityDebounceTimer = null;

// Helper function to broadcast native user activity event (debounced)
const broadcastUserActivity = () => {
  // Clear any existing timer
  if (activityDebounceTimer) {
    clearTimeout(activityDebounceTimer);
  }

  // Set a new timer to debounce rapid fires
  activityDebounceTimer = setTimeout(() => {
    nativeBroadcast('fareharbor.native.user.activity', 'userWebActivity', {});
    activityDebounceTimer = null;
  }, 100); // 100ms debounce delay
};

const props = defineProps({
  booking: {
    type: Object,
    default: () => ({}),
  },
  company: {
    type: Object,
    default: () => ({}),
  },
  waiver: {
    type: Object,
    default: () => ({}),
  },
  previewMode: {
    type: Boolean,
    default: false,
  },
  isLoadingSubmit: {
    type: Boolean,
    default: false,
  },
  isDockDevice: {
    type: Boolean,
    default: false,
  },
});

const waiverForm = ref({
  firstName: '',
  lastName: '',
  email: '',
  dob: null,
  phoneNumber: '',

  isMarketingChecked: props.waiver.features?.isMarketingConsentEnabledByDefault,
  isAcceptanceChecked: false,

  addressLine: '',
  city: '',
  stateProvince: '',
  postcode: '',
  country: '',

  emergencyContact: {
    firstName: '',
    lastName: '',
    phoneNumber: '',
    addressLine: '',
    city: '',
    stateProvince: '',
    postcode: '',
    country: '',
  },

  isMinorChecked: false,
  minors: [],

  isAdditionalParticipantChecked: false,
  additionalParticipants: [],

  driversLicenseNumber: '',
  driversLicenseIssuer: '',
  isDriversLicenseChecked: false,

  // Body acknowledgement (when sections exist, body is Section 1)
  bodyAck: false,
  bodyInitials: '',
  // Per-section acknowledgements — bound via bracket-path names below
  sectionAcknowledgements: (props.waiver.sections || []).map(() => ({
    acknowledged: false,
    initials: '',
    key: generateUuid(),
  })),
});

const refWaiverForm = ref(null);

const signatureCanvas = ref();
const signaturePad = ref();
const isShowTermsModal = ref(false);

const allFormErrors = ref({});
const clientErrors = ref({ ...EMPTY_ERRORS_FOR_FIELDS });

const countries = computed(() => formatCountriesToSelectOptions());
const locale = localStorage.getItem('waiverAppLang');

const isHeightInUSAFormat = computed(
  () => props.waiver.features?.heightData?.unit === US_HEIGHT_UNIT,
);

watch(
  () => waiverForm.value.isMinorChecked,
  (value) => {
    refWaiverForm.value.setValues({
      minors: value
        ? [
            {
              firstName: '',
              lastName: '',
              dob: null,
              key: generateUuid(),
            },
          ]
        : [],
    });
  },
);

watch(
  () => waiverForm.value.isAdditionalParticipantChecked,
  (value) => {
    waiverForm.value.additionalParticipants = value
      ? [
          {
            firstName: '',
            lastName: '',
            key: generateUuid(),
          },
        ]
      : [];
  },
);

const resizeCanvas = () => {
  const ratio = Math.max(window.devicePixelRatio || 1, 1);

  signatureCanvas.value.width = signatureCanvas.value.offsetWidth * ratio;
  signatureCanvas.value.height = signatureCanvas.value.offsetHeight * ratio;
  signatureCanvas.value.getContext('2d').scale(ratio, ratio);

  signaturePad.value.fromData(signaturePad.value.toData());
};

onMounted(() => {
  if (signatureCanvas.value) {
    signaturePad.value = new SignaturePad(signatureCanvas.value);
    signaturePad.value.addEventListener('beginStroke', () => {
      clientErrors.value.signatureCanvas = '';
      broadcastUserActivity();
    });
    signaturePad.value.addEventListener('endStroke', () => {
      broadcastUserActivity();
    });

    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();
  }
  // Watch for changes in main contact date of birth
  watch(
    () => waiverForm.value.dob,
    () => {
      broadcastUserActivity();
    },
  );

  // Watch for changes in minors array and their date of birth values
  watch(
    () => waiverForm.value.minors,
    () => {
      broadcastUserActivity();
    },
    { deep: true },
  );
});

onUnmounted(() => {
  window.removeEventListener('resize', resizeCanvas);
  // Clear debounce timer on unmount
  if (activityDebounceTimer) {
    clearTimeout(activityDebounceTimer);
    activityDebounceTimer = null;
  }
});

const maxDate = computed(() => moment().format('YYYY-MM-DD'));

const isDriversLicenseRequired = computed(
  () =>
    waiverForm.value.driversLicenseNumber ||
    waiverForm.value.driversLicenseIssuer,
);

const openPolicies = () => {
  isShowTermsModal.value = true;
};

const onCloseTermsModal = () => {
  isShowTermsModal.value = false;
};

const isNotValidOtherFields = () => {
  let isError = false;

  if (
    props.waiver.signatureType === 'handwritten' &&
    signaturePad.value?.isEmpty() // signaturePad.value doesn't exist if signatureType is not 'handwritten'
  ) {
    clientErrors.value.signatureCanvas = T('Signature is required');
    isError = true;
  }

  allFormErrors.value = {
    ...allFormErrors.value,
    ...clientErrors.value,
  };

  return isError;
};

const onClickClearCanvasButton = () => {
  track('click-fh-waiver-form-clear-handwritten-signature');

  signaturePad.value.clear();
};

const onClickAddMinor = () => {
  waiverForm.value.minors.push({
    firstName: '',
    lastName: '',
    dob: null,
    key: generateUuid(),
  });
};

const onClickDeleteMinor = (idx) => {
  waiverForm.value.minors.splice(idx, 1);
};

const onClickAddAdditionalParticipant = () => {
  waiverForm.value.additionalParticipants.push({
    firstName: '',
    lastName: '',
    key: generateUuid(),
  });
};

const onClickDeleteAdditionalParticipant = (idx) => {
  waiverForm.value.additionalParticipants.splice(idx, 1);
};

const trackFormErrors = () => {
  const validationErrors = separateFormErrors(allFormErrors.value);

  track('click-fh-waiver-form-submit', {
    successful: false,
    'minors-checkbox': waiverForm.value.isMinorChecked,
    'additional-participants-checkbox':
      waiverForm.value.isAdditionalParticipantChecked,
    'marketing-checkbox': waiverForm.value.isMarketingChecked,
    'unsuccess-reason': {
      'validation-error': validationErrors,
    },
  });
};

const onErrorForm = (errors, { scrollToFirstError }) => {
  allFormErrors.value = { ...errors };

  isNotValidOtherFields();
  trackFormErrors();
  scrollToFirstError();
};

const submitForm = async (value) => {
  allFormErrors.value = {};

  if (isNotValidOtherFields()) {
    trackFormErrors();

    return;
  }

  const formatMinors = (value.minors || []).map(({ key, ...rest }) => ({
    ...rest,
  }));

  const formatAdditionalParticipants = (value.additionalParticipants || []).map(
    ({ key, ...rest }) => ({
      ...rest,
    }),
  );

  const formattedHeight = isHeightInUSAFormat.value
    ? value.heightFeet * 12 + value.heightInches
    : value.height;

  const {
    isDriversLicenseChecked,
    bodyAck,
    bodyInitials,
    sectionAcknowledgements,
    ...rest
  } = value;

  const hasSections = props.waiver.sections?.length > 0;
  const ackType = props.waiver.features?.sectionAcknowledgementType;

  // Note: keys inside `sectionAcknowledgements` array items are intentionally
  // snake_case (`section_index`) because `transformKeys(formData, toSnakeCase)`
  // in `signWaiver` does not recurse into arrays.
  const formattedSectionAcknowledgements = hasSections
    ? (sectionAcknowledgements || []).map(({ key, ...ack }, i) => ({
        section_index: i,
        acknowledged: ackType === 'checkbox' ? !!ack.acknowledged : false,
        initials: ackType === 'initials' ? ack.initials || '' : '',
      }))
    : undefined;

  const signWaiverFormData = {
    waiverUuid: props.waiver.uuid,
    waiverModifiedAt: props.waiver.modifiedAt,
    handwrittenSvgData: signaturePad.value?.toDataURL('image/svg+xml'),
    userTimezoneOffset: new Date().getTimezoneOffset(),
    ...rest,
    minors: formatMinors,
    additionalParticipants: formatAdditionalParticipants,
    height: formattedHeight,
    ...(formattedSectionAcknowledgements && {
      sectionAcknowledgements: formattedSectionAcknowledgements,
    }),
    bodyAcknowledged: hasSections && ackType === 'checkbox' ? !!bodyAck : null,
    bodyInitials:
      hasSections && ackType === 'initials' ? bodyInitials || '' : null,
  };

  emit('submit', signWaiverFormData);
};

const signatureBlockText = computed(() =>
  props.waiver.signatureType === 'acceptance_checkbox'
    ? T('Acceptance')
    : T('Signature'),
);

const getContactDetailsTitleName = computed(() => {
  if (props.waiver.features?.isAdditionalParticipantsEnabled) {
    return T('Main contact details');
  }

  return waiverForm.value.isMinorChecked
    ? T('Parent or guardian contact details')
    : T('Contact details');
});

const getMinorItemTitleName = (idx) =>
  waiverForm.value.minors.length > 1
    ? interpolate(T('Minor %(number)s details'), {
        number: idx + 1,
      })
    : T('Minor’s details');

const getAdditionalParticipantItemTitleName = (idx) =>
  waiverForm.value.additionalParticipants.length > 1
    ? interpolate(T('Additional participant %(number)s details'), {
        number: idx + 1,
      })
    : T("Additional participant's details");

const computedMinorText = computed(() => {
  const requiredLegalAge =
    props.waiver.features?.requiredLegalAge || defaultRequiredLegalAge;
  return requiredLegalAge === defaultRequiredLegalAge
    ? T('This waiver is for a minor (under 18 years old)')
    : interpolate(
        T('This waiver is for a minor (under %(requiredLegalAge)s years old)'),
        { requiredLegalAge },
      );
});

const computedDobValidationMessage = computed(() => {
  const requiredLegalAge =
    props.waiver.features?.requiredLegalAge || defaultRequiredLegalAge;
  return requiredLegalAge === defaultRequiredLegalAge
    ? T('You must be at least 18 to sign')
    : interpolate(T('You must be at least %(requiredLegalAge)s to sign'), {
        requiredLegalAge,
      });
});
</script>

<style scoped lang="scss">
.flex-container {
  display: flex;
}

.content-block {
  display: flex;
  flex-direction: column;
  gap: var(--fh-space-lg);
}

.ack-block {
  padding-top: 0;
}

.minor-content-container {
  display: flex;
  flex-direction: column;
  gap: var(--fh-space-2xl);
}

.additional-participant-content-container {
  display: flex;
  flex-direction: column;
  gap: var(--fh-space-2xl);
}

.minor-content-container:not(:last-child),
.additional-participant-content-container:not(:last-child),
.additional-info-block:not(:last-child) {
  border-bottom: var(--fh-border-sm) solid var(--fh-color-border-default);
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.names-block {
  display: flex;
  column-gap: var(--fh-space-sm);
}

.names-block > div {
  flex: 1;
}

.address-block-container {
  display: flex;
  flex-direction: column;
  gap: var(--fh-space-sm);
}

.submit-button-container {
  display: flex;
  justify-content: center;
}

.signature-subtitle {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--fh-space-md);
}

.signature-canvas-container {
  display: grid;
  gap: var(--fh-space-xs);
  width: 100%;
}

.signature-canvas {
  width: 100%;
  height: 180px;
  border-radius: var(--fh-radius-lg);
  box-shadow: inset 0 0 0 1px var(--fh-color-border-input-default);
}

.signature-canvas-invalid {
  box-shadow: inset 0 0 0 1px var(--fh-color-border-error-default);
}

.signature-canvas-validation-message {
  display: flex;
  align-items: start;
  gap: var(--fh-space-xs);

  & > span {
    margin: 1px 0px;
  }
}

.half-width-container {
  flex: 0.5;
  padding-right: var(--fh-size-sm);
}

.minor-block-item-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.additional-participant-block-item-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.waiver-form {
  margin-bottom: 72px;
}

@media only screen and (max-width: 679px) {
  .waiver-form {
    margin-bottom: var(--fh-size-3xl);
  }

  .names-block {
    flex-direction: column;
    gap: var(--fh-space-lg);
  }

  .address-block-container {
    gap: var(--fh-space-lg);
  }

  .submit-button {
    width: 100%;
  }

  .half-width-container {
    flex: 1;
    padding-right: 0;
  }
}

.text-link {
  text-decoration: underline;
  cursor: pointer;
}

.two-inputs-height-block {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: var(--fh-space-sm);
}

.height-inches-input-wrapper {
  margin-top: 24px;
}

.height-feet-input-wrapper,
.height-inches-input-wrapper {
  flex: 0.5;
}
</style>
